import { useNavigate } from "react-router-dom";
import { Boxes, FileText, HeartHandshake, Briefcase, Mail } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useBrands } from "../brands/queries";
import { useBlogPosts } from "../blog/queries";
import { useSocialProjects } from "../social/queries";
import { useJobListings } from "../jobs/queries";
import { useContactStats } from "../contacts/queries";
import { QueryError } from "../../components/Common/QueryError";

interface StatCardProps {
  label: string;
  value: number | string;
  icon: LucideIcon;
  to: string;
  loading: boolean;
  accent?: boolean;
}

function StatCard({ label, value, icon: Icon, to, loading, accent }: StatCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      onClick={() => navigate(to)}
      className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(to)}
      role="button"
      aria-label={label}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground truncate mb-3">{label}</p>
            <p
              className={`text-4xl font-bold tracking-tight ${
                accent ? "text-primary" : "text-foreground"
              }`}
            >
              {loading ? (
                <span className="inline-block w-10 h-9 rounded bg-muted animate-pulse" />
              ) : (
                value
              )}
            </p>
          </div>
          <div
            className={`flex-shrink-0 rounded-lg p-2.5 ${
              accent
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
            } transition-colors`}
          >
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const brandsQuery = useBrands();
  const postsQuery = useBlogPosts();
  const socialQuery = useSocialProjects();
  const jobsQuery = useJobListings(true);
  const contactQuery = useContactStats();
  const { data: brands, isLoading: brandsLoading } = brandsQuery;
  const { data: posts, isLoading: postsLoading } = postsQuery;
  const { data: socialProjects, isLoading: socialLoading } = socialQuery;
  const { data: activeJobs, isLoading: jobsLoading } = jobsQuery;
  const { data: contactStats, isLoading: contactLoading } = contactQuery;

  const allQueries = [brandsQuery, postsQuery, socialQuery, jobsQuery, contactQuery];
  const failed = allQueries.filter((q) => q.isError && q.data === undefined);
  const retryFailed = () => failed.forEach((q) => void q.refetch());
  // Yüklenemeyen sayaç "0" yerine "—" gösterilir; 0 veri kaybı gibi görünür.
  const show = (q: { isError: boolean; data: unknown }, value: number) =>
    q.isError && q.data === undefined ? "—" : value;

  const stats: StatCardProps[] = [
    {
      label: "Markalar",
      value: show(brandsQuery, brands?.length ?? 0),
      icon: Boxes,
      to: "/brands",
      loading: brandsLoading,
    },
    {
      label: "Blog Yazıları",
      value: show(postsQuery, posts?.length ?? 0),
      icon: FileText,
      to: "/blog",
      loading: postsLoading,
    },
    {
      label: "Sosyal Sorumluluk",
      value: show(socialQuery, socialProjects?.length ?? 0),
      icon: HeartHandshake,
      to: "/social",
      loading: socialLoading,
    },
    {
      label: "Aktif İş İlanları",
      value: show(jobsQuery, activeJobs?.length ?? 0),
      icon: Briefcase,
      to: "/jobs",
      loading: jobsLoading,
    },
    {
      label: "Okunmamış Mesajlar",
      value: show(contactQuery, contactStats?.unread ?? 0),
      icon: Mail,
      to: "/contacts",
      loading: contactLoading,
      accent: (contactStats?.unread ?? 0) > 0,
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Gösterge Paneli</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tüm içeriklerinize hızlı erişim ve canlı sayılar.
        </p>
      </div>

      {failed.length > 0 && (
        <QueryError
          error={failed[0].error}
          onRetry={retryFailed}
          fallback="Bazı sayılar yüklenemedi."
          className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm"
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>
    </div>
  );
}
